const GreekTaxInvoiceTypeEnum = require('@/lib/Tax/Countries/Greece/GreekTaxInvoiceTypeEnum');
const GreekTaxLine = require('@/lib/Tax/Countries/Greece/GreekTaxLine');
const TaxReceiptRequiredReasonEnum = require('@/lib/Tax/TaxReceiptRequiredReasonEnum');
const TaxSummary = require('@/lib/Tax/TaxSummary');
const { cartExceedsVatThreshold, orderExceedsVatThreshold } = require('@/lib/Tax/Countries/UK');
const { container } = require('@/container');
const { extractTaxSummaryGroupByKey, groupTaxSummaryGroupsByKey, sortTaxSummaryGroupsByRate } = require('@/lib/Tax/index');
const { hydrate } = require('@/lib/Hydration');
const { suppressModalCancelledErrorOrReThrow } = require('@/lib/Errors');

/**
 * @constructor
 *
 * @param {ApiClient} apiClient
 * @param {AgentService} agentService
 * @param {CountryManager} countryManager
 * @param {UserService} userService
 */
const TaxManager = function (
    apiClient,
    agentService,
    countryManager,
    userService,
) {
    this.apiClient = apiClient;
    this.agentService = agentService;
    this.countryManager = countryManager;
    this.userService = userService;
};

TaxManager.prototype = {
    /**
     * @param {string} reason
     */
    showTaxReceiptRequiredAlert(reason) {
        container.dialogService.alert(
            'TAX.TAX_RECEIPT_REQUIRED__' + reason,
            {
                icon: 'fa-file-invoice',
                title: 'TAX.TAX_RECEIPT_REQUIRED_TITLE',
            }
        )
            .then(() => {})
            .catch(suppressModalCancelledErrorOrReThrow);
    },

    /**
     * @param {string} reason
     */
    showTaxReceiptUnavailableAlert(reason) {
        container.dialogService.alert(
            'TAX.TAX_RECEIPT_UNAVAILABLE__' + reason,
            {
                icon: 'fa-file-invoice',
                title: 'TAX.TAX_RECEIPT_UNAVAILABLE_TITLE',
            }
        )
            .then(() => {})
            .catch(suppressModalCancelledErrorOrReThrow);
    },

    /**
     * @param {Company} company
     * @param {Order} order
     *
     * @return {string|false} Returns a string for the reason why, or false.
     */
    mustPrintTaxReceiptForOrder(company, order) {
        if (!company.taxEnabled) {
            return false;
        }

        // Check if this is a company with UK tax (VAT) enabled and the order exceeds the threshold.
        // More checks can be added in this function for other countries.
        if (this.isVatEnabledForCompany(company)) {
            if (orderExceedsVatThreshold(order)) {
                return TaxReceiptRequiredReasonEnum.ORDER_IS_OVER_LIMIT;
            }

            if (order.exceededVatLimit) {
                return TaxReceiptRequiredReasonEnum.ORDER_WAS_OVER_LIMIT;
            }
        }

        return false;
    },

    /**
     * @param {Company} company
     * @param {Cart} cart
     *
     * @return {string|false} Returns a string for the reason why, or false.
     */
    mustPrintTaxReceiptForCart(company, cart) {
        if (!company.taxEnabled) {
            return false;
        }

        // Check if this is a company with UK tax (VAT) enabled and the order exceeds the threshold.
        // More checks can be added in this function for other countries.
        if (this.isVatEnabledForCompany(company)) {
            // Force a VAT receipt if the value of the cart is over the limit.
            if (cartExceedsVatThreshold(cart)) {
                return TaxReceiptRequiredReasonEnum.CART_IS_OVER_LIMIT;
            }

            // Force a VAT receipt if this is an order edit, and the order being edited was ever over the limit.
            if (cart.originalOrder && cart.originalOrder.exceededVatLimit) {
                return TaxReceiptRequiredReasonEnum.ORDER_WAS_OVER_LIMIT;
            }
        }

        return false;
    },

    /**
     * Check if UK tax (VAT) is enabled for the given company.
     *
     * @param {Company} company
     *
     * @return {boolean}
     */
    isVatEnabledForCompany(company) {
        if (!company.taxEnabled) {
            return false;
        }

        const gb = this.countryManager.findByCode('GB');

        const gbTaxReg = company.getTaxRegistrationCodeForCountry(gb);

        return !!(gbTaxReg && gbTaxReg.registrationCode);
    },

    /**
     * Fetch a TaxSummary from the API for a booking (an order including any adjustments).
     *
     * This API endpoint returns the data already summarized so there is not much to do but
     * stick it into a TaxSummary model and return it.
     *
     * @param {string} bookingRef
     *
     * @return {Promise<TaxSummary>}
     */
    getBookingTaxSummary(bookingRef) {
        return this.apiClient
            .request(
                'GET',
                `bookings/${bookingRef}/tax`
            )
            .then((response) => {
                if (response && response.data) {
                    return hydrate(response.data, new TaxSummary());
                }
                throw new Error('Failed to get booking tax summary.');
            });
    },

    /**
     * Fetch a TaxSummary from the API for an individual order (ignoring any prior or subsequent adjustments).
     *
     * This is much more complicated than getting the booking tax summary (above).
     * Here the API does not return a summary, instead it returns the invoice lines and credit note lines for an order.
     *
     * @param {number} orderID
     *
     * @return {Promise<TaxSummary>}
     */
    getOrderTaxSummary(orderID) {
        return this.apiClient
            .request(
                'GET',
                `orders/${orderID}/tax`
            )
            .then((response) => {
                if (response && response.data) {
                    return hydrate(response.data, new TaxSummary());
                }
                throw new Error('Failed to get order tax summary.');
            });
    },

    /**
     * Load the tax information for an entire booking (with adjustments included) and apply it to an order.
     * Sets the 'taxSummary' on the order and on its lines.
     *
     * @param {Order} order
     */
    async addBookingTaxToOrder(order) {
        if (!this.userService.getCompany().taxEnabled) {
            // Only load the tax if tax processing is enabled for the company.
            return;
        }

        const taxSummary = await this.getBookingTaxSummary(
            order.originalRef ? order.originalRef : order.bookingRef
        );

        this.populateLettersForTaxSummary(taxSummary);

        this.applyTaxSummaryToOrder(taxSummary, order);
    },

    /**
     * Load the tax information for a specific order.
     * Sets the 'taxSummary' on the order and on its lines.
     *
     * @param {Order} order
     */
    async addOrderTaxToOrder(order) {
        if (!this.userService.getCompany().taxEnabled) {
            // Only load the tax if tax processing is enabled for the company.
            return;
        }

        const taxSummary = await this.getOrderTaxSummary(order.ID);
        this.applyTaxSummaryToOrder(taxSummary, order);
    },

    /**
     * @param {OrderAdjustmentResponse} orderAdjustmentResponse
     */
    async addTaxToOrderAdjustment(orderAdjustmentResponse) {
        if (!this.userService.getCompany().taxEnabled) {
            // Only load the tax if tax processing is enabled for the company.
            return;
        }

        /**
         * @type {TaxSummary}
         */
        let adjustmentOrderTaxSummary;

        /**
         * @type {TaxSummary}
         */
        let adjustedOrderTaxSummary;

        await Promise.allSettled([
            this.getOrderTaxSummary(orderAdjustmentResponse.adjustmentOrder.ID)
                .then((taxSummary) => {
                    adjustmentOrderTaxSummary = taxSummary;
                }),

            this.getBookingTaxSummary(orderAdjustmentResponse.adjustedOrder.originalRef)
                .then((taxSummary) => {
                    adjustedOrderTaxSummary = taxSummary;
                }),
        ]);

        // Use the same letters for the two tax summaries.
        adjustmentOrderTaxSummary.taxRateLetters = adjustedOrderTaxSummary.taxRateLetters;
        this.populateLettersForTaxSummary(adjustedOrderTaxSummary);
        this.populateLettersForTaxSummary(adjustmentOrderTaxSummary);

        this.applyTaxSummaryToOrder(adjustedOrderTaxSummary, orderAdjustmentResponse.adjustedOrder);
        this.applyTaxSummaryToOrder(adjustmentOrderTaxSummary, orderAdjustmentResponse.adjustmentOrder);
    },

    /**
     * This will generate a 'letter' representing each tax rate found in the tax summary, and set it in the
     * taxSummary.taxRateLetters object.
     * Note that it does not assign that letter to each tax summary group yet.
     *
     * @param {TaxSummary} taxSummary
     */
    populateLettersForTaxSummary(taxSummary) {
        taxSummary.taxSummaryGroups.forEach((taxSummaryGroup) => {
            taxSummary.taxRateLetters.addRate(taxSummaryGroup.taxRate);
        });
    },

    /**
     * @private
     *
     * @param {TaxSummary} taxSummary
     * @param {Order} order
     */
    applyTaxSummaryToOrder(taxSummary, order) {
        // Store the tax summary model on the order.
        order.taxSummary = taxSummary;

        // The API returns the tax summary groups grouped by
        // type, taxType, taxRate, and taxRegistrationCode.
        // When we print the table on the receipt which shows the breakdown of tax by rate we only want to
        // print one line per tax rate. So re-group the tax summary groups only by taxRate.
        // TODO: Make a parameter in the API to say which fields we want to group by.
        const overallTaxSummaryGroupsByRate = groupTaxSummaryGroupsByKey(taxSummary.taxSummaryGroups, 'taxRate');
        sortTaxSummaryGroupsByRate(overallTaxSummaryGroupsByRate);

        // Set the letter for each order-level tax summary group.
        overallTaxSummaryGroupsByRate.forEach((taxSummaryGroup) => {
            taxSummaryGroup.letter = taxSummary.taxRateLetters.getLetterForRate(taxSummaryGroup.taxRate);
        });

        // Process the line-level tax summaries.
        // We don't re-group line-level tax summary groups by taxRate because it's unnecessary. That's only
        // needed for printing the tax table on the receipt.
        taxSummary.lines.forEach((lineTaxSummary) => {
            // Sort the groups for this line (/these lines) so that the letters are alphabetical.
            sortTaxSummaryGroupsByRate(lineTaxSummary.taxSummaryGroups);

            // Set the letter for each line-level tax summary group.
            lineTaxSummary.taxSummaryGroups.forEach((taxSummaryGroup) => {
                taxSummaryGroup.letter = taxSummary.taxRateLetters.getLetterForRate(taxSummaryGroup.taxRate);
            });
        });

        // Attach the line-level tax summaries to the lines in the order.
        order.getLines().forEach((orderLine) => {
            const lineTaxSummary = taxSummary.getLineByOrdersItemsID(orderLine.ID);
            if (lineTaxSummary) {
                orderLine.taxSummary = lineTaxSummary;
            } else {
                console.error('Failed to find a tax summary for an order line.', orderLine);
            }
        });

        // Apply the tax summary to the order.
        order.taxSummary.taxSummaryGroupsByTaxRate = overallTaxSummaryGroupsByRate;
    },

    /**
     * @param {Company} company
     *
     * @returns {?string}
     */
    getGreekTaxRegistrationCodeFromCompany(company) {
        let greece = this.countryManager.findByCode('GR');
        let greekTaxRegistration = company.getTaxRegistrationCodeForCountry(greece);

        return greekTaxRegistration && greekTaxRegistration.registrationCode
            ? greekTaxRegistration.registrationCode
            : null;
    },

    /**
     * Create the data needed for the "SL Line" (aka "e.txt") required for Greek Tax / Casio Tax Manager integrations.
     * See the attached doc at https://digitickets.atlassian.net/browse/PRO-495
     *
     * The format is a single line starting with <%SL; with each field separated by semicolons ending in >.
     *
     * e.g. <%SL;144476123;;123321456654;;;;;222;A;20;0.00;100.00;100.00;0.00;0.00;0.00;13.00;24.00;0.00;237.00;0;>
     *
     * The fields are as follows. The number in brackets is the length of the field.
     *
     * 1    Issuer's VAT    (12) NOT SENT BY APPLICATION
     * 2    Recipient's VAT    (12)
     * 3    EAFDSS S/N    (12)  NOT SENT BY APPLICATION
     * 4    Customer's Receipt Collection Card Number    (19)
     * 5    Date and Time    (12)  NOT SENT BY APPLICATION
     * 6    Serial Number of Daily Signatures    (5)  NOT SENT BY APPLICATION
     * 7    Serial Number of Successive Signatures    (5) NOT SENT BY APPLICATION
     * 8    Ζ Number    (4)  NOT SENT BY APPLICATION
     * 9    Type of Invoice    (4)
     * 10   Validation Series    (10)
     * 11   Number of Invoice    (10)
     * 12   Net Amount Α (6%)    (18:2)
     * 13   Net Amount Β (13%)    (18:2)
     * 14   Net Amount C (24%)    (18:2)
     * 15   Net Amount D (36%)    (18:2)
     * 16   Net Amount Ε (0%)   (18:2)
     * 17   VAT Α (6%)     (18:2)
     * 18   VAT Β (13%) (18:2)
     * 19   VAT C (24%)    (18:2)
     * 20   VAT D (36%)    (18:2)
     * 21   Total Amount of Invoice    (18:2)
     * 22   Currency Code    (1)
     *
     * @param {Order} order
     * @param {TaxSummaryGroup[]} orderTaxSummaryGroups
     * @param {boolean=false} isCancellation
     * @param {?number} cancelledOrderID
     *
     * @return {GreekTaxLine}
     */
    createGreekTaxLine(
        order,
        orderTaxSummaryGroups,
        isCancellation = false,
        cancelledOrderID = null
    ) {
        const e = new GreekTaxLine();

        /* eslint-disable line-comment-position */

        e.setField(2, null); // Customer ("client") VAT number.
        e.setField(4, null); // Customer's Receipt Collection Card Number. Don't know what this means.

        // Type of invoice. There are 4 known values:are 222 (invoice) and 173 (retail POS receipt)
        // - 222 'Invoice' (unused) The customer's VAT number should go in field 2
        // - 173 'Retail POS Receipt'
        // - 502 'Reprint'
        // - 215 'Cancelling Order'
        // Line number 9 -> GreekTaxLineFieldNumberEnum.INVOICE_TYPE (see tests)
        e.setField(
            9,
            isCancellation ? GreekTaxInvoiceTypeEnum.CANCELLING_ORDER : GreekTaxInvoiceTypeEnum.RETAIL_POS_RECEIPT
        );

        // Validation Series
        // Attention should be given to field 9 (Type of Invoice).
        // This field receives values based on the type of invoice according to Table D ‘INVOICE CODES’ pursuant POL 1221.
        // Additional attention should be given to the invoice code ‘215’ (SPECIAL CANCELLING ELEMENT) where the sign of the total amount is opposite to the amount of the invoice cancelled.
        // Also in cancellation invoices with invoice code “215”, after the validation series you must add information of the relevant invoice that is canceled:
        // ; Validation Series of cancellation invoice # type code of invoice # number of the relevant invoice # validation series of invoice;
        let validationSeries = 'A';
        if (isCancellation) {
            validationSeries = [
                'B',
                GreekTaxInvoiceTypeEnum.RETAIL_POS_RECEIPT,
                cancelledOrderID,
                'A',
            ].join('#');
        }
        e.setField(10, validationSeries);

        // Line number 11: Number of Invoice
        e.setField(11, order.ID);

        // The 'tax types' are named A/B/C/D/E rather than by the % in case the rates change in future.
        const taxA = extractTaxSummaryGroupByKey(orderTaxSummaryGroups, 'taxTypeName', 'Greece A');
        const taxB = extractTaxSummaryGroupByKey(orderTaxSummaryGroups, 'taxTypeName', 'Greece B');
        const taxC = extractTaxSummaryGroupByKey(orderTaxSummaryGroups, 'taxTypeName', 'Greece C');
        const taxD = extractTaxSummaryGroupByKey(orderTaxSummaryGroups, 'taxTypeName', 'Greece D');
        const taxE = extractTaxSummaryGroupByKey(orderTaxSummaryGroups, 'taxTypeName', 'Greece E');

        // Note that for refund orders these amounts will already be supplied by the DT API as negative. They should
        // be negative on the line on the receipt.
        e.setField(12, taxA ? taxA.net : 0.00); // Net Amount Α (6%)
        e.setField(13, taxB ? taxB.net : 0.00); // Net Amount B (13%)
        e.setField(14, taxC ? taxC.net : 0.00); // Net Amount C (24%)
        e.setField(15, taxD ? taxD.net : 0.00); // Net Amount D (36%)
        e.setField(16, taxE ? taxE.net : 0.00); // Net Amount Ε (0%)
        e.setField(17, taxA ? taxA.tax : 0.00); // VAT Α (6%)
        e.setField(18, taxB ? taxB.tax : 0.00); // VAT Β (13%)
        e.setField(19, taxC ? taxC.tax : 0.00); // VAT C (24%)
        e.setField(20, taxD ? taxD.tax : 0.00); // VAT D (36%)
        // Line number 21 -> GreekTaxLineFieldNumberEnum.TOTAL_AMOUNT_OF_INVOICE (see tests)
        e.setField(21, order.total); // Total Amount of Invoice (including tax)
        e.setField(22, 0); // Currency Code. This field is unused - zero = blank.

        /* eslint-enable line-comment-position */

        return e;
    },

    /**
     * According to Michael at EAMH System to produce an SL line for a duplicate receipt:
     * In the copy/duplicate, you can send the <%SL…> script with Invoice Type Code "502"
     * and all the amounts in the fields should be "0.00"
     *
     * @param {Order} order
     *
     * @return {GreekTaxLine}
     */
    createGreekTaxLineForReprint(order) {
        const e = new GreekTaxLine();

        /* eslint-disable line-comment-position */

        e.setField(2, null); // Customer ("client") VAT number.
        e.setField(4, null); // Customer's Receipt Collection Card Number.
        // Line number 9 -> GreekTaxLineFieldNumberEnum.INVOICE_TYPE (see tests)
        e.setField(9, GreekTaxInvoiceTypeEnum.REPRINT); // Type of invoice. 502 = reprint apparently.
        e.setField(10, 'A'); // Validation Services.
        // Line number 11 -> GreekTaxLineFieldNumberEnum.NUMBER_OF_INVOICE (see tests)
        e.setField(11, order.ID); // Number of invoice
        e.setField(12, 0.00); // Net Amount Α (6%)
        e.setField(13, 0.00); // Net Amount B (13%)
        e.setField(14, 0.00); // Net Amount C (24%)
        e.setField(15, 0.00); // Net Amount D (36%)
        e.setField(16, 0.00); // Net Amount Ε (0%)
        e.setField(17, 0.00); // VAT Α (6%)
        e.setField(18, 0.00); // VAT Β (13%)
        e.setField(19, 0.00); // VAT C (24%)
        e.setField(20, 0.00); // VAT D (36%)
        // Line number 21 -> GreekTaxLineFieldNumberEnum.TOTAL_AMOUNT_OF_INVOICE (see tests)
        e.setField(21, 0.00); // Total Amount of Invoice (including tax)
        e.setField(22, 0); // Currency Code

        /* eslint-enable line-comment-position */

        return e;
    },

    /**
     * Returns a GreekTaxLine model for the order order, using the tax breakdown for the given registration code.
     *
     * @param {Order} order
     * @param {OrderAdjustmentResponse|null} orderAdjustmentResponse
     * @param {string} taxRegistrationCode
     *
     * @return {Promise<GreekTaxLine|null>}
     */
    async getTaxSummaryAndCreateGreekTaxLine(
        order,
        orderAdjustmentResponse,
        taxRegistrationCode
    ) {
        // Load the tax summary for the order if it is not already loaded.
        if (!order.taxSummary) {
            await this.addOrderTaxToOrder(order);
        }

        let orderTaxSummaryGroups = order.taxSummary.taxSummaryGroups.filter(
            (group) => group.taxRegistrationCode === taxRegistrationCode
        );
        // Check there is at least one taxSummaryGroup with the Greek tax registration. If not, don't do Greek tax.
        if (orderTaxSummaryGroups.length < 1) {
            return null;
        }

        orderTaxSummaryGroups = groupTaxSummaryGroupsByKey(orderTaxSummaryGroups, 'taxTypeName');

        if (order.receiptPrinted) {
            // A receipt has already been printed for this order, so we are printing a duplicate receipt.
            // Create a duplicate receipt line.
            return this.createGreekTaxLineForReprint(order);
        }

        if (orderAdjustmentResponse && orderAdjustmentResponse.orderIsFullyCancelled()) {
            // This is an order adjustment which fully removes all the items from the order.
            // Create a reversal receipt line.
            return this.createGreekTaxLine(
                order,
                orderTaxSummaryGroups,
                true,
                orderAdjustmentResponse.originalOrder.ID
            );
        }

        // Create a new sale receipt line.
        return this.createGreekTaxLine(
            order,
            orderTaxSummaryGroups
        );
    },
};

module.exports = TaxManager;
